import { z } from 'zod';
import type { TrainerPackageBalance } from './trainer-package-purchase.js';
import type { ClientNote } from './client-notes.js';
import type { EntityVerificationStatus, OwnerEntityVerificationState } from './verification.js';

/**
 * F34 — trainer-dashboard composite read shapes + the trainer-side affiliation
 * surface (display order + per-venue stats). The venue↔trainer affiliation
 * MANAGEMENT (invite/accept/decline/revoke) ships in F13 — F34 reuses it and
 * only adds the trainer-dashboard ordering + stats here.
 */

/* ─── Dashboard home composite ─────────────────────────────────────────────── */

export type TrainerScheduleItem = {
  readonly bookingId: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly sportName: string;
  readonly status: string;
  readonly clientUserId: string | null;
  readonly clientDisplayName: string | null;
  readonly locationKind: string | null;
  readonly venueId: string | null;
  readonly venueName: string | null;
};

export type TrainerPackageAlert = {
  readonly purchaseId: string;
  readonly clientUserId: string;
  readonly clientDisplayName: string | null;
  readonly sessionsRemaining: number;
  readonly expiresAt: string | null;
};

export type TrainerDashboardHome = {
  readonly pendingRequestCount: number;
  readonly todaySchedule: readonly TrainerScheduleItem[];
  readonly incomeAtAGlance: {
    readonly currency: string;
    readonly grossThisMonth: number;
    readonly sessionsThisMonth: number;
    /** Per-day gross for the trailing window, for the KPI sparkline. */
    readonly sparkline: readonly number[];
  };
  readonly packageAlerts: readonly TrainerPackageAlert[];
  /** F09 v2 (E3) — the 2-valued trainer tick column; render from `verification`. */
  readonly verificationStatus: EntityVerificationStatus;
  /** F09 v2 — the trainer tick's owner state (replaces the dead F33 banners). */
  readonly verification: OwnerEntityVerificationState;
};

/* ─── Client list + detail ─────────────────────────────────────────────────── */

/** De-duped client = a user who has booked this trainer at least once. */
export type TrainerClientListItem = {
  readonly clientUserId: string;
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
  readonly sessionCount: number;
  readonly lastSessionAt: string | null;
  readonly activePackageCount: number;
};

export type TrainerClientDetail = {
  readonly clientUserId: string;
  readonly displayName: string | null;
  readonly avatarUrl: string | null;
  readonly sessionHistory: readonly TrainerScheduleItem[];
  readonly packageBalances: readonly TrainerPackageBalance[];
  /** Trainer-private note (owner-only). NULL if none written yet. */
  readonly note: ClientNote | null;
};

/* ─── Affiliation surface (trainer-dashboard view of F13 affiliations) ─────── */

/** Trainer's multi-venue display ordering — full ordered list of affiliation ids. */
export const SetAffiliationDisplayOrderInputSchema = z
  .object({ affiliationIds: z.array(z.string().uuid()).max(100) })
  .strict();
export type SetAffiliationDisplayOrderInput = z.infer<typeof SetAffiliationDisplayOrderInputSchema>;

export type TrainerAffiliationStats = {
  readonly affiliationId: string;
  readonly venueId: string;
  readonly venueName: string | null;
  readonly sessionCount: number;
  readonly grossAmount: number;
  readonly currency: string;
};
